import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Network, Wallet, Users, Building, TrendingUp, LogOut, Copy, Plus,
  ArrowUpRight, ArrowDownLeft, Clock, CreditCard, IndianRupee, Home,
  FileText, User, Menu, X, Phone, Mail, Award, Calendar, ChevronRight,
  Share2, Link, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const agentApi = api;

const getRankColor = rank => ({
  'Bronze': 'bg-amber-700 text-white', 'Silver': 'bg-slate-400 text-white',
  'Gold': 'bg-yellow-500 text-white', 'Platinum': 'bg-slate-600 text-white',
  'Diamond': 'bg-cyan-500 text-white'
}[rank] || 'bg-slate-500 text-white');

export default function AgentPortal() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [teamTree, setTeamTree] = useState(null);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({ property_id: '', customer_name: '', customer_mobile: '', customer_address: '', relation_type: '', relation_name: '', booking_amount: '', payment_mode: 'Cash' });
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankForm, setBankForm] = useState({ bank_name: '', account_number: '', ifsc_code: '', account_holder_name: '' });
  
  const RELATION_TYPES = [
    { value: 'father', label: 'Father (पिता)' },
    { value: 'husband', label: 'Husband (पति)' },
    { value: 'mother', label: 'Mother (माता)' },
    { value: 'wife', label: 'Wife (पत्नी)' },
    { value: 'son', label: 'Son (पुत्र)' },
    { value: 'daughter', label: 'Daughter (पुत्री)' },
    { value: 'brother', label: 'Brother (भाई)' },
    { value: 'sister', label: 'Sister (बहन)' },
    { value: 'guardian', label: 'Guardian (अभिभावक)' },
    { value: 'c/o', label: 'C/O (देखभाल)' },
    { value: 'other', label: 'Other (अन्य)' }
  ];

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await agentApi.get('/agent/dashboard');
      setDashboard(response.data);
      setAgent(response.data.agent);
      setBankForm({ bank_name: response.data.agent.bank_name || '', account_number: response.data.agent.account_number || '', ifsc_code: response.data.agent.ifsc_code || '', account_holder_name: response.data.agent.account_holder_name || '' });
    } catch (error) {
      if (error.response?.status === 401) { localStorage.removeItem('agent_token'); navigate('/agent-login'); }
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await fetchDashboard();
      const [propsRes, bookingsRes, txRes] = await Promise.all([
        agentApi.get('/agent/properties'), agentApi.get('/agent/bookings'), agentApi.get('/agent/wallet/transactions')
      ]);
      setProperties(propsRes.data); setBookings(bookingsRes.data); setTransactions(txRes.data.transactions || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, [fetchDashboard]);

  useEffect(() => { if (!localStorage.getItem('agent_token')) { navigate('/agent-login'); return; } fetchData(); }, [navigate, fetchData]);

  const fetchTeamTree = async () => { try { const r = await agentApi.get('/agent/team-tree'); setTeamTree(r.data); } catch (e) { console.error(e); } };
  const handleLogout = () => { localStorage.removeItem('agent_token'); localStorage.removeItem('agent_data'); navigate('/agent-login'); };
  const copyReferralCode = () => { if (agent) { navigator.clipboard.writeText(agent.referral_code); toast.success('Referral code copied!'); } };
  
  const copyReferralLink = () => {
    if (agent) {
      const referralUrl = `${window.location.origin}/agent/register/${agent.referral_code}`;
      navigator.clipboard.writeText(referralUrl);
      toast.success('Referral link copied! Share it to add new agents under you.');
    }
  };
  
  const shareReferralLink = async () => {
    if (agent) {
      const referralUrl = `${window.location.origin}/agent/register/${agent.referral_code}`;
      const shareText = `Join Ganesa Partner Network and start earning! Register using my referral link: ${referralUrl}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join Ganesa Partner Network',
            text: shareText,
            url: referralUrl
          });
        } catch (e) {
          copyReferralLink();
        }
      } else {
        copyReferralLink();
      }
    }
  };

  const handleCreateBooking = async e => {
    e.preventDefault();
    try {
      await agentApi.post('/agent/bookings', { ...bookingForm, booking_amount: parseFloat(bookingForm.booking_amount) });
      toast.success('Booking created! Commission will be released after payment & registry.');
      setBookingDialogOpen(false);
      setBookingForm({ property_id: '', customer_name: '', customer_mobile: '', customer_address: '', relation_type: '', relation_name: '', booking_amount: '', payment_mode: 'Cash' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create booking'); }
  };

  const handleWithdraw = async e => {
    e.preventDefault();
    try {
      await agentApi.post('/agent/wallet/withdraw', { amount: parseFloat(withdrawAmount) });
      toast.success('Withdrawal request submitted!');
      setWithdrawDialogOpen(false); setWithdrawAmount(''); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateBank = async e => {
    e.preventDefault();
    try { await agentApi.put('/agent/bank-details', bankForm); toast.success('Bank details updated!'); setBankDialogOpen(false); fetchDashboard(); }
    catch (error) { toast.error('Failed to update bank details'); }
  };

  const selectedProperty = properties.find(p => p.id === bookingForm.property_id);

  if (loading || !dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Mobile Dashboard View
  const MobileDashboard = () => (
    <div className="space-y-4 pb-20">
      {/* Welcome Card */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-emerald-100 text-sm">Welcome back,</p>
            <h2 className="text-xl font-bold">{agent.name}</h2>
          </div>
          <div className="text-right">
            <Badge className={getRankColor(agent.rank) + ' text-xs'}>{agent.rank}</Badge>
            <p className="text-xs text-emerald-100 mt-1">{agent.referral_code}</p>
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
          <p className="text-emerald-100 text-sm">Wallet Balance</p>
          <p className="text-3xl font-bold">₹{(dashboard.wallet.balance || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <Users className="w-6 h-6 mx-auto text-blue-500 mb-2" />
          <p className="text-2xl font-bold text-slate-800">{dashboard.team.total_count}</p>
          <p className="text-xs text-slate-500">Team</p>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <Building className="w-6 h-6 mx-auto text-amber-500 mb-2" />
          <p className="text-2xl font-bold text-slate-800">{dashboard.sales.total_bookings}</p>
          <p className="text-xs text-slate-500">Bookings</p>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <TrendingUp className="w-6 h-6 mx-auto text-purple-500 mb-2" />
          <p className="text-2xl font-bold text-slate-800">₹{((dashboard.wallet.total_earned || 0) / 1000).toFixed(0)}K</p>
          <p className="text-xs text-slate-500">Earned</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <button 
          onClick={() => setBookingDialogOpen(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl p-4 flex items-center gap-3 transition-all active:scale-95"
        >
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <Plus className="w-5 h-5" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm">New Booking</p>
            <p className="text-xs text-emerald-100">Create sale</p>
          </div>
        </button>
        <button 
          onClick={shareReferralLink}
          className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl p-4 flex items-center gap-3 transition-all active:scale-95"
        >
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <UserPlus className="w-5 h-5" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm">Add Agent</p>
            <p className="text-xs text-blue-100">Share link</p>
          </div>
        </button>
      </div>
      
      {/* Referral Link Card */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between mb-2">
          <p className="font-semibold flex items-center gap-2"><Link className="w-4 h-4" /> Your Referral Link</p>
          <Badge className="bg-white/20 text-white text-xs">{agent.referral_code}</Badge>
        </div>
        <p className="text-xs text-indigo-100 mb-3 truncate">
          {window.location.origin}/agent/register/{agent.referral_code}
        </p>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            className="flex-1 bg-white/20 hover:bg-white/30 text-white"
            onClick={copyReferralLink}
          >
            <Copy className="w-3 h-3 mr-1" /> Copy Link
          </Button>
          <Button 
            size="sm" 
            className="flex-1 bg-white text-indigo-600 hover:bg-indigo-50"
            onClick={shareReferralLink}
          >
            <Share2 className="w-3 h-3 mr-1" /> Share
          </Button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-600" /> Recent Transactions
        </h3>
        {dashboard.recent_transactions.length === 0 ? (
          <p className="text-center text-slate-400 py-6 text-sm">No transactions yet</p>
        ) : (
          <div className="space-y-2">
            {dashboard.recent_transactions.slice(0, 5).map(tx => (
              <div key={tx.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.amount > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                    {tx.amount > 0 ? <ArrowDownLeft className="text-green-600 w-4 h-4" /> : <ArrowUpRight className="text-red-600 w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700 line-clamp-1">{tx.description}</p>
                    <p className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <p className={`font-bold text-sm ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {tx.amount > 0 ? '+' : ''}₹{Math.abs(tx.amount).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Mobile Bookings View
  const MobileBookings = () => (
    <div className="space-y-4 pb-20">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-slate-800">My Bookings</h2>
        <Button size="sm" className="bg-emerald-600" onClick={() => setBookingDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>
      
      {/* Commission Info Card */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm font-medium text-amber-800">Commission Release Flow</p>
        <div className="flex items-center gap-1 mt-2 text-xs">
          <span className="px-2 py-1 bg-yellow-200 rounded">Booking</span>
          <ChevronRight className="w-3 h-3 text-slate-400" />
          <span className="px-2 py-1 bg-purple-200 rounded">Payment</span>
          <ChevronRight className="w-3 h-3 text-slate-400" />
          <span className="px-2 py-1 bg-blue-200 rounded">Registry</span>
          <ChevronRight className="w-3 h-3 text-slate-400" />
          <span className="px-2 py-1 bg-green-200 rounded">Commission</span>
        </div>
      </div>
      
      {bookings.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center shadow-sm">
          <Building className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No bookings yet</p>
          <Button className="mt-4 bg-emerald-600" onClick={() => setBookingDialogOpen(true)}>Create First Booking</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map(b => {
            // Calculate progress step
            let step = 1;
            if (b.payment_status === 'paid') step = 2;
            if (b.registry_status === 'completed') step = 3;
            if (b.commission_status === 'released') step = 4;
            
            return (
              <div key={b.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${b.commission_status === 'released' ? 'border-2 border-green-300' : ''}`}>
                {/* Progress Bar */}
                <div className="h-1 bg-slate-100 flex">
                  <div className={`h-full transition-all ${step >= 1 ? 'bg-yellow-500' : 'bg-slate-200'}`} style={{width: '25%'}} />
                  <div className={`h-full transition-all ${step >= 2 ? 'bg-purple-500' : 'bg-slate-200'}`} style={{width: '25%'}} />
                  <div className={`h-full transition-all ${step >= 3 ? 'bg-blue-500' : 'bg-slate-200'}`} style={{width: '25%'}} />
                  <div className={`h-full transition-all ${step >= 4 ? 'bg-green-500' : 'bg-slate-200'}`} style={{width: '25%'}} />
                </div>
                
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-slate-800">{b.property_name}</p>
                      <p className="text-sm text-slate-500">{b.customer_name} • {b.customer_mobile}</p>
                    </div>
                    <Badge className={
                      b.commission_status === 'released' ? 'bg-green-500 text-white text-xs' :
                      b.commission_status === 'eligible' ? 'bg-blue-500 text-white text-xs' :
                      b.payment_status === 'paid' ? 'bg-purple-500 text-white text-xs' :
                      'bg-yellow-500 text-white text-xs'
                    }>
                      {b.commission_status === 'released' ? 'Complete' : 
                       b.commission_status === 'eligible' ? 'Registry Pending' : 
                       b.payment_status === 'paid' ? 'Payment Done' : 'Payment Pending'}
                    </Badge>
                  </div>
                  
                  {/* Status Steps */}
                  <div className="grid grid-cols-4 gap-1 mb-3">
                    <div className={`text-center p-2 rounded ${step >= 1 ? 'bg-yellow-50' : 'bg-slate-50'}`}>
                      <div className={`w-5 h-5 rounded-full mx-auto mb-1 flex items-center justify-center text-xs ${step >= 1 ? 'bg-yellow-500 text-white' : 'bg-slate-200'}`}>1</div>
                      <p className="text-xs text-slate-600">Booked</p>
                    </div>
                    <div className={`text-center p-2 rounded ${step >= 2 ? 'bg-purple-50' : 'bg-slate-50'}`}>
                      <div className={`w-5 h-5 rounded-full mx-auto mb-1 flex items-center justify-center text-xs ${step >= 2 ? 'bg-purple-500 text-white' : 'bg-slate-200'}`}>2</div>
                      <p className="text-xs text-slate-600">Paid</p>
                    </div>
                    <div className={`text-center p-2 rounded ${step >= 3 ? 'bg-blue-50' : 'bg-slate-50'}`}>
                      <div className={`w-5 h-5 rounded-full mx-auto mb-1 flex items-center justify-center text-xs ${step >= 3 ? 'bg-blue-500 text-white' : 'bg-slate-200'}`}>3</div>
                      <p className="text-xs text-slate-600">Registry</p>
                    </div>
                    <div className={`text-center p-2 rounded ${step >= 4 ? 'bg-green-50' : 'bg-slate-50'}`}>
                      <div className={`w-5 h-5 rounded-full mx-auto mb-1 flex items-center justify-center text-xs ${step >= 4 ? 'bg-green-500 text-white' : 'bg-slate-200'}`}>4</div>
                      <p className="text-xs text-slate-600">Done</p>
                    </div>
                  </div>
                  
                  {/* Amount Details */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-400">Total</p>
                      <p className="font-bold text-emerald-600">₹{(b.total_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Paid</p>
                      <p className="font-semibold text-slate-700">₹{(b.paid_amount || b.booking_amount || 0).toLocaleString()}</p>
                      {b.balance_amount > 0 && (
                        <p className="text-xs text-red-500">Bal: ₹{b.balance_amount.toLocaleString()}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Commission</p>
                      <p className={`font-bold ${b.commission_status === 'released' ? 'text-green-600' : 'text-slate-400'}`}>
                        ₹{(b.total_commission || 0).toLocaleString()}
                      </p>
                      {b.commission_status !== 'released' && (
                        <p className="text-xs text-slate-400">(Pending)</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Registry Info */}
                  {b.registry_number && (
                    <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                      Registry: {b.registry_number} • {b.registry_date ? new Date(b.registry_date).toLocaleDateString() : ''}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Mobile Team View
  const MobileTeam = () => (
    <div className="space-y-4 pb-20">
      <h2 className="text-lg font-bold text-slate-800">My Team</h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <Users className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-2xl font-bold">{dashboard.team.direct_count}</p>
          <p className="text-emerald-100 text-sm">Direct</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <Network className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-2xl font-bold">{dashboard.team.total_count}</p>
          <p className="text-blue-100 text-sm">Total Team</p>
        </div>
      </div>
      
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Team Members</h3>
          <Button variant="ghost" size="sm" onClick={fetchTeamTree}>Refresh</Button>
        </div>
        {!teamTree ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : teamTree.tree.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No team members yet</p>
            <button onClick={copyReferralCode} className="mt-3 text-emerald-600 text-sm font-medium">
              Share your code: {agent.referral_code}
            </button>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-auto">
            {teamTree.tree.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm">
                  {m.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{m.name}</p>
                  <p className="text-xs text-slate-500">{m.referral_code}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getRankColor(m.rank) + ' text-xs'}>{m.rank}</Badge>
                  <Badge variant="outline" className="text-xs">L{m.level}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Mobile Wallet View
  const MobileWallet = () => (
    <div className="space-y-4 pb-20">
      <h2 className="text-lg font-bold text-slate-800">Wallet</h2>
      
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-5 text-white">
        <p className="text-emerald-100 text-sm">Available Balance</p>
        <p className="text-4xl font-bold mb-4">₹{(dashboard.wallet.balance || 0).toLocaleString()}</p>
        <div className="grid grid-cols-2 gap-3">
          <Button 
            className="bg-white text-emerald-700 hover:bg-emerald-50"
            onClick={() => setWithdrawDialogOpen(true)}
          >
            <IndianRupee className="w-4 h-4 mr-2" /> Withdraw
          </Button>
          <Button 
            variant="outline" 
            className="border-white/50 text-white hover:bg-white/10"
            onClick={() => setBankDialogOpen(true)}
          >
            <CreditCard className="w-4 h-4 mr-2" /> Bank
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <ArrowDownLeft className="w-5 h-5 text-green-500 mb-2" />
          <p className="text-xl font-bold text-green-600">₹{(dashboard.wallet.total_earned || 0).toLocaleString()}</p>
          <p className="text-xs text-slate-500">Total Earned</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <ArrowUpRight className="w-5 h-5 text-red-500 mb-2" />
          <p className="text-xl font-bold text-red-600">₹{(dashboard.wallet.total_withdrawn || 0).toLocaleString()}</p>
          <p className="text-xs text-slate-500">Withdrawn</p>
        </div>
      </div>

      {/* Pending Alert */}
      {dashboard.wallet.pending_withdrawal > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-800">Withdrawal Pending</p>
            <p className="text-xs text-amber-600">₹{dashboard.wallet.pending_withdrawal.toLocaleString()} awaiting approval</p>
          </div>
        </div>
      )}

      {/* Transactions */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-3">Transaction History</h3>
        {transactions.length === 0 ? (
          <p className="text-center text-slate-400 py-6 text-sm">No transactions</p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-auto">
            {transactions.map(tx => (
              <div key={tx.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{tx.description}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{tx.type}</Badge>
                    <span className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <p className={`font-bold text-sm ml-2 ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {tx.amount > 0 ? '+' : ''}₹{Math.abs(tx.amount).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Profile Sheet
  const ProfileSheet = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-4 pb-4 border-b">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-2xl font-bold">
          {agent.name?.charAt(0)}
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-800">{agent.name}</h3>
          <Badge className={getRankColor(agent.rank)}>{agent.rank}</Badge>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">{agent.email}</span>
        </div>
        <div className="flex items-center gap-3">
          <Phone className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">{agent.phone}</span>
        </div>
        <div className="flex items-center gap-3">
          <Award className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">Referral: {agent.referral_code}</span>
          <Button size="sm" variant="ghost" onClick={copyReferralCode}><Copy className="w-3 h-3" /></Button>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">Joined: {new Date(agent.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <Button variant="outline" className="w-full mt-4 text-red-600 border-red-200" onClick={handleLogout}>
        <LogOut className="w-4 h-4 mr-2" /> Logout
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="bg-white border-b sticky top-0 z-40 md:hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Network className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-800">Agent Portal</span>
          </div>
          <button onClick={() => setProfileOpen(true)} className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm">
            {agent.name?.charAt(0)}
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="bg-white border-b sticky top-0 z-50 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Network className="w-5 h-5 text-white" />
            </div>
            <div><h1 className="font-bold text-slate-800">Agent Portal</h1><p className="text-xs text-slate-500">Ganesa Partner Network</p></div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium text-sm">{agent.name}</p>
              <div className="flex items-center gap-2">
                <Badge className={getRankColor(agent.rank)}>{agent.rank}</Badge>
                <code className="text-xs bg-slate-100 px-2 py-0.5 rounded cursor-pointer" onClick={copyReferralCode}>{agent.referral_code}</code>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}><LogOut size={16} /></Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 md:max-w-7xl md:mx-auto md:py-6">
        {/* Mobile Content */}
        <div className="md:hidden">
          {activeTab === 'dashboard' && <MobileDashboard />}
          {activeTab === 'bookings' && <MobileBookings />}
          {activeTab === 'team' && <MobileTeam />}
          {activeTab === 'wallet' && <MobileWallet />}
        </div>

        {/* Desktop Content - Original Design */}
        <div className="hidden md:block">
          <div className="flex gap-2 mb-6 bg-white p-1 rounded-xl shadow-sm">
            {['dashboard', 'bookings', 'team', 'wallet'].map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); if (tab === 'team') fetchTeamTree(); }}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${activeTab === tab ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <CardContent className="pt-6"><Wallet size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">₹{(dashboard.wallet.balance || 0).toLocaleString()}</p><p className="text-emerald-100 text-sm">Wallet Balance</p></CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="pt-6"><Users size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">{dashboard.team.total_count}</p><p className="text-blue-100 text-sm">Team Members</p></CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardContent className="pt-6"><Building size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">{dashboard.sales.total_bookings}</p><p className="text-amber-100 text-sm">Total Bookings</p></CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardContent className="pt-6"><TrendingUp size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">₹{((dashboard.wallet.total_earned || 0) / 1000).toFixed(0)}K</p><p className="text-purple-100 text-sm">Total Earned</p></CardContent>
                </Card>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="cursor-pointer hover:shadow-md" onClick={() => setBookingDialogOpen(true)}>
                  <CardContent className="pt-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><Plus className="text-emerald-600" /></div>
                    <div><p className="font-semibold">Create New Booking</p><p className="text-sm text-slate-500">Book property for a customer</p></div>
                  </CardContent>
                </Card>
                <Card className="cursor-pointer hover:shadow-md" onClick={copyReferralCode}>
                  <CardContent className="pt-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"><Copy className="text-blue-600" /></div>
                    <div><p className="font-semibold">Share Referral Code</p><p className="text-sm text-slate-500">{agent.referral_code} - Click to copy</p></div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          
          {activeTab === 'bookings' && <MobileBookings />}
          {activeTab === 'team' && <MobileTeam />}
          {activeTab === 'wallet' && <MobileWallet />}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t md:hidden z-40 safe-area-pb">
        <div className="flex justify-around py-2">
          {[
            { id: 'dashboard', icon: Home, label: 'Home' },
            { id: 'bookings', icon: FileText, label: 'Bookings' },
            { id: 'team', icon: Users, label: 'Team' },
            { id: 'wallet', icon: Wallet, label: 'Wallet' },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); if (item.id === 'team') fetchTeamTree(); }}
              className={`flex flex-col items-center py-1 px-4 rounded-lg transition-all ${activeTab === item.id ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              <item.icon className={`w-5 h-5 ${activeTab === item.id ? 'text-emerald-600' : ''}`} />
              <span className="text-xs mt-1">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Profile Dialog - Mobile */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Profile</DialogTitle></DialogHeader>
          <ProfileSheet />
        </DialogContent>
      </Dialog>

      {/* Create Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create New Booking</DialogTitle><DialogDescription>Book a property for your customer</DialogDescription></DialogHeader>
          <form onSubmit={handleCreateBooking} className="space-y-4">
            <div>
              <Label>Select Property *</Label>
              <Select value={bookingForm.property_id} onValueChange={v => setBookingForm({ ...bookingForm, property_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose a property" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_name} - ₹{(p.total_price || 0).toLocaleString()}</SelectItem>)}</SelectContent>
              </Select>
              {selectedProperty && <p className="text-sm text-emerald-600 mt-1">Commission: ₹{(selectedProperty.estimated_commission || 0).toLocaleString()}</p>}
            </div>
            <div><Label>Customer Name *</Label><Input value={bookingForm.customer_name} onChange={e => setBookingForm({ ...bookingForm, customer_name: e.target.value })} required /></div>
            <div><Label>Customer Mobile *</Label><Input value={bookingForm.customer_mobile} onChange={e => setBookingForm({ ...bookingForm, customer_mobile: e.target.value })} required /></div>
            <div><Label>Customer Address</Label><Input value={bookingForm.customer_address} onChange={e => setBookingForm({ ...bookingForm, customer_address: e.target.value })} /></div>
            
            {/* Guardian/Relation Section */}
            <div className="pt-3 border-t border-slate-200">
              <Label className="flex items-center gap-2 text-slate-700 mb-2">
                <Users className="w-4 h-4" /> Guardian / Relation
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Select value={bookingForm.relation_type} onValueChange={v => setBookingForm({ ...bookingForm, relation_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select relation" /></SelectTrigger>
                  <SelectContent>
                    {RELATION_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Name" value={bookingForm.relation_name} onChange={e => setBookingForm({ ...bookingForm, relation_name: e.target.value })} />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Booking Amount *</Label><Input type="number" value={bookingForm.booking_amount} onChange={e => setBookingForm({ ...bookingForm, booking_amount: e.target.value })} required /></div>
              <div><Label>Payment Mode</Label>
                <Select value={bookingForm.payment_mode} onValueChange={v => setBookingForm({ ...bookingForm, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Create Booking</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request Withdrawal</DialogTitle><DialogDescription>Available: ₹{((dashboard.wallet.balance || 0) - (dashboard.wallet.pending_withdrawal || 0)).toLocaleString()}</DialogDescription></DialogHeader>
          <form onSubmit={handleWithdraw} className="space-y-4">
            <div><Label>Amount (₹)</Label><Input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} max={(dashboard.wallet.balance || 0) - (dashboard.wallet.pending_withdrawal || 0)} required /></div>
            {(!agent.bank_name || !agent.account_number) && <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">Please update bank details first</p>}
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={!agent.bank_name || !agent.account_number}>Submit Request</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Update Bank Details</DialogTitle><DialogDescription>For withdrawals</DialogDescription></DialogHeader>
          <form onSubmit={handleUpdateBank} className="space-y-4">
            <div><Label>Bank Name *</Label><Input value={bankForm.bank_name} onChange={e => setBankForm({ ...bankForm, bank_name: e.target.value })} required /></div>
            <div><Label>Account Number *</Label><Input value={bankForm.account_number} onChange={e => setBankForm({ ...bankForm, account_number: e.target.value })} required /></div>
            <div><Label>IFSC Code *</Label><Input value={bankForm.ifsc_code} onChange={e => setBankForm({ ...bankForm, ifsc_code: e.target.value.toUpperCase() })} required /></div>
            <div><Label>Account Holder Name *</Label><Input value={bankForm.account_holder_name} onChange={e => setBankForm({ ...bankForm, account_holder_name: e.target.value })} required /></div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Save Bank Details</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
